import {
  type ElementType,
  type ForwardedRef,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
} from 'react'
import {
  Box,
  useTheme,
  useMediaQuery,
  OverridableComponentProps,
  SystemProps,
  Theme,
  CommonProps,
  Breakpoint,
} from '@tino-frontend/coreui'
import { DrawerRoot, DrawerHandleRoot, DialogRoot } from './float.styles'
import clsx from 'clsx'
import { useUtilityClasses } from './float.classes'

export type FloatEvent = {
  open: boolean
}

type Props = {
  /**
   * Determine the max-width of the dialog.
   * The dialog width grows with the size of the screen.
   * Set to `false` to disable `maxWidth`.
   * @default 'sm'
   */
  maxWidth?: Breakpoint | false
  /**
   * The content of the component.
   */
  children?: ReactNode
  /**
   * Show or hide the modal
   */
  open?: boolean
  /**
   * Top right element on the modal
   */
  action?: ReactNode
  /**
   * Callback when the drawer open or closes
   */
  onChange?: (event: FloatEvent) => void
  /**
   * Disable the default padding of the drawer and dialog
   */
  disablePadding?: boolean
  /**
   * Background color for the drawer or dialog.
   */
  backgroundColor?: string
  /**
   * If true, prevents the modal from closing when clicking outside of it.
   * Default is false, which allows closing when clicking outside.
   * @default false
   */
  disableOutsideClose?: boolean
} & SystemProps<Theme> &
  CommonProps

export type FloatProps<C extends ElementType> = OverridableComponentProps<
  C,
  Props
>

type OverridableComponent = <C extends ElementType = 'div'>(
  props: FloatProps<C>
) => ReactNode

const Float: OverridableComponent = forwardRef(function Float<
  C extends ElementType = 'div',
>(inProps: FloatProps<C>, ref: ForwardedRef<HTMLDivElement>) {
  const {
    type = 'bottom',
    children,
    open = false,
    action,
    onChange = () => null,
    variant = 'persistent',
    className,
    maxWidth,
    disablePadding = false,
    disableOutsideClose = false,
    backgroundColor,
    ...props
  } = inProps

  const ownerState = {
    ...props,
    type,
    variant,
    children,
    backgroundColor,
  }

  const [openArea, setOpenArea] = useState<boolean>(open)
  const theme = useTheme()
  const enableBottom = useMediaQuery(theme.breakpoints.down('md'))

  const classes = useUtilityClasses(ownerState)

  useEffect(() => {
    setOpenArea(open)
  }, [open])

  function drawerChange(open: boolean) {
    onChange({ open })

    if (disableOutsideClose) return

    setOpenArea(open)
  }

  function isBottomType() {
    return enableBottom && type === 'bottom'
  }

  // TODO: Remover o as any desse ponto quando atualizarmos o typescript no coreui
  return isBottomType() ? (
    <Box
      {...(props as any)}
      {...(!open && { display: 'none' })}
      ref={ref}
      className={clsx(className, classes.root)}
    >
      <DrawerRoot
        data-testid="drawer-root"
        anchor="bottom"
        className={clsx({
          [classes.open]: openArea,
          [classes.close]: !openArea,
        })}
        open={openArea}
        onClose={() => drawerChange(false)}
        onOpen={() => drawerChange(true)}
        maxHeight={props.maxHeight}
        disablePadding={disablePadding}
        backgroundColor={backgroundColor}
      >
        <DrawerHandleRoot />
        <div className={classes.action}>{action}</div>
        <div className={classes.children}>{children}</div>
      </DrawerRoot>
    </Box>
  ) : (
    <DialogRoot
      sx={props.sx ?? {}}
      {...(!open && { display: 'none' })}
      className={clsx(className, classes.root)}
      maxWidth={maxWidth}
      data-testid="drawer-root"
      open={openArea}
      onClose={() => drawerChange(false)}
      classes={{
        paper: classes.paper,
      }}
      disablePadding={disablePadding}
      style={{ backgroundColor }}
    >
      {action && <div className={classes.action}>{action}</div>}
      <div className={classes.children}>{children}</div>
    </DialogRoot>
  )
})

export default Float
